// Token pagination helpers for DynamoDB-backed list resolvers.
//
// Those resolvers paginate over the underlying scan, not over the matches: a
// page can come back with zero items *and* a non-null nextToken when the
// slice of the table DynamoDB read held nothing matching the filter. Treating
// "no items" as "end of list" therefore hides rows that live further down the
// table — and infinite-scroll sentinels, which only fire when appending a
// page changes layout, stall permanently on such a page.

/** Shape of every `listX` connection returned by the AppSync APIs. */
export interface TokenPage<TItem> {
  items: TItem[];
  nextToken?: string | null;
}

/**
 * Upper bound on requests spent resolving one logical page. Needing more than
 * this to surface a single row means the resolver is doing a filtered scan
 * that should be a GSI query — bail out instead of hammering AppSync from the
 * browser. The exhausted page keeps its nextToken so callers can resume.
 */
export const MAX_PAGE_REQUESTS = 25;

/**
 * Fetch the next page that actually carries items, following nextToken past
 * any empty pages. Resolves with the first non-empty page, or with the last
 * page seen when the list is genuinely exhausted (no nextToken).
 */
export async function fetchNonEmptyPage<TItem>(
  fetchPage: (nextToken?: string) => Promise<TokenPage<TItem>>,
  startToken?: string,
  maxRequests: number = MAX_PAGE_REQUESTS,
): Promise<TokenPage<TItem>> {
  let nextToken = startToken;

  for (let request = 0; request < maxRequests; request++) {
    const page = await fetchPage(nextToken);
    const items = page.items ?? [];

    // Non-empty page, or the end of the list — either way, hand it back.
    if (items.length > 0 || !page.nextToken) {
      return { ...page, items };
    }

    nextToken = page.nextToken;
  }

  console.warn(
    `[pagination] ${maxRequests} consecutive empty pages; stopping and keeping the cursor`,
  );
  return { items: [], nextToken };
}
